import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const API_BASE_URL = "";

export default function BillPaymentLimits() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  
  const [limits, setLimits] = useState({
    // Global Limits
    global: {
      dailyLimit: 100000,
      weeklyLimit: 500000,
      monthlyLimit: 2000000,
      quarterlyLimit: 5000000,
      perTransactionMin: 50,
      perTransactionMax: 50000,
      isEnabled: true
    },
    // Airtime Limits
    airtime: {
      dailyLimit: 50000,
      weeklyLimit: 200000,
      monthlyLimit: 500000,
      quarterlyLimit: 1500000,
      perTransactionMin: 50,
      perTransactionMax: 10000,
      isEnabled: true
    },
    // Data Limits
    data: {
      dailyLimit: 50000,
      weeklyLimit: 200000,
      monthlyLimit: 500000,
      quarterlyLimit: 1500000,
      perTransactionMin: 100,
      perTransactionMax: 20000,
      isEnabled: true
    },
    // Electricity Limits
    electricity: {
      dailyLimit: 100000,
      weeklyLimit: 400000,
      monthlyLimit: 1500000,
      quarterlyLimit: 4000000,
      perTransactionMin: 500,
      perTransactionMax: 100000,
      isEnabled: true
    },
    // Cable TV Limits
    cableTv: {
      dailyLimit: 50000,
      weeklyLimit: 150000,
      monthlyLimit: 500000,
      quarterlyLimit: 1500000,
      perTransactionMin: 500,
      perTransactionMax: 50000,
      isEnabled: true
    }
  });

  const [activeTab, setActiveTab] = useState("global");

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    };
  };

  useEffect(() => {
    fetchLimits();
  }, []);

  const fetchLimits = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/vtu/limits`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        if (data.data) {
          setLimits(data.data);
        }
      }
    } catch (error) {
      console.error("Error fetching limits:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLimitChange = (category, field, value) => {
    const numValue = value === "" ? 0 : parseInt(value.replace(/,/g, ""), 10);
    setLimits(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: isNaN(numValue) ? 0 : numValue
      }
    }));
  };

  const handleToggle = (category) => {
    setLimits(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        isEnabled: !prev[category].isEnabled
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/vtu/limits`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(limits)
      });

      if (!response.ok) {
        throw new Error("Failed to save limits");
      }

      setSuccess("Transaction limits saved successfully!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (error) {
      console.error("Error saving limits:", error);
      setError("Failed to save limits. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: "global", name: "Global Limits", icon: "🌐", color: "#1183FB" },
    { id: "airtime", name: "Airtime", icon: "📱", color: "#10b981" },
    { id: "data", name: "Data", icon: "📶", color: "#8b5cf6" },
    { id: "electricity", name: "Electricity", icon: "⚡", color: "#f59e0b" },
    { id: "cableTv", name: "Cable TV", icon: "📺", color: "#ef4444" }
  ];

  const currentTab = tabs.find(t => t.id === activeTab);
  const currentLimits = limits[activeTab];

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>Loading limits...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .tab-btn:hover { transform: translateY(-2px); }
        .limit-input:focus { border-color: #1183FB; outline: none; box-shadow: 0 0 0 3px rgba(17, 131, 251, 0.2); }
        .toggle-switch { position: relative; width: 50px; height: 26px; }
        .toggle-switch input { opacity: 0; width: 0; height: 0; }
        .toggle-slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background-color: #cbd5e1; transition: 0.3s; border-radius: 26px; }
        .toggle-slider:before { position: absolute; content: ""; height: 20px; width: 20px; left: 3px; bottom: 3px; background-color: white; transition: 0.3s; border-radius: 50%; }
        input:checked + .toggle-slider { background-color: #10b981; }
        input:checked + .toggle-slider:before { transform: translateX(24px); }
      `}</style>

      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <button onClick={() => navigate("/admin")} style={styles.backBtn}>←</button>
          <div>
            <h1 style={styles.headerTitle}>Bill Payment Limits</h1>
            <p style={styles.headerSubtitle}>Configure transaction limits for bill payments</p>
          </div>
        </div>
        <button onClick={handleSave} style={styles.saveBtn} disabled={saving}>
          {saving ? "Saving..." : "💾 Save Changes"}
        </button>
      </header>

      <main style={styles.main}>
        {/* Alerts */}
        {success && <div style={styles.successAlert}>✅ {success}</div>}
        {error && <div style={styles.errorAlert}>❌ {error}</div>}

        {/* Info Card */}
        <div style={styles.infoCard}>
          <div style={styles.infoIcon}>ℹ️</div>
          <div>
            <h4 style={styles.infoTitle}>How Limits Work</h4>
            <p style={styles.infoText}>
              Set daily, weekly, monthly, and quarterly limits for bill payments. 
              Global limits apply to all transaction types. Individual service limits override global limits when lower.
              Users will be blocked from making transactions that exceed these limits.
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div style={styles.tabsContainer}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              className="tab-btn"
              onClick={() => setActiveTab(tab.id)}
              style={{
                ...styles.tabBtn,
                background: activeTab === tab.id ? tab.color : "white",
                color: activeTab === tab.id ? "white" : "#64748b",
                borderColor: activeTab === tab.id ? tab.color : "#e2e8f0"
              }}
            >
              <span style={styles.tabIcon}>{tab.icon}</span>
              {tab.name}
            </button>
          ))}
        </div>

        {/* Limits Configuration */}
        <div style={styles.configCard}>
          <div style={styles.configHeader}>
            <div style={styles.configTitleRow}>
              <span style={{...styles.configIcon, background: `${currentTab.color}20`, color: currentTab.color}}>
                {currentTab.icon}
              </span>
              <div>
                <h3 style={styles.configTitle}>{currentTab.name} Configuration</h3>
                <p style={styles.configSubtitle}>Set transaction limits for {currentTab.name.toLowerCase()}</p>
              </div>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={currentLimits.isEnabled}
                onChange={() => handleToggle(activeTab)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          {!currentLimits.isEnabled && (
            <div style={styles.disabledOverlay}>
              <p>⚠️ {currentTab.name} limits are disabled. Enable to configure.</p>
            </div>
          )}

          <div style={{...styles.limitsGrid, opacity: currentLimits.isEnabled ? 1 : 0.5, pointerEvents: currentLimits.isEnabled ? "auto" : "none"}}>
            {/* Period Limits */}
            <div style={styles.limitSection}>
              <h4 style={styles.sectionTitle}>📅 Period Limits</h4>
              <div style={styles.limitRow}>
                <div style={styles.limitField}>
                  <label style={styles.limitLabel}>Daily Limit</label>
                  <div style={styles.inputWrapper}>
                    <span style={styles.inputPrefix}>₦</span>
                    <input
                      type="text"
                      className="limit-input"
                      style={styles.limitInput}
                      value={currentLimits.dailyLimit.toLocaleString()}
                      onChange={(e) => handleLimitChange(activeTab, "dailyLimit", e.target.value)}
                    />
                  </div>
                  <span style={styles.limitHint}>Max per day</span>
                </div>
                <div style={styles.limitField}>
                  <label style={styles.limitLabel}>Weekly Limit</label>
                  <div style={styles.inputWrapper}>
                    <span style={styles.inputPrefix}>₦</span>
                    <input
                      type="text"
                      className="limit-input"
                      style={styles.limitInput}
                      value={currentLimits.weeklyLimit.toLocaleString()}
                      onChange={(e) => handleLimitChange(activeTab, "weeklyLimit", e.target.value)}
                    />
                  </div>
                  <span style={styles.limitHint}>Max per week</span>
                </div>
              </div>
              <div style={styles.limitRow}>
                <div style={styles.limitField}>
                  <label style={styles.limitLabel}>Monthly Limit</label>
                  <div style={styles.inputWrapper}>
                    <span style={styles.inputPrefix}>₦</span>
                    <input
                      type="text"
                      className="limit-input"
                      style={styles.limitInput}
                      value={currentLimits.monthlyLimit.toLocaleString()}
                      onChange={(e) => handleLimitChange(activeTab, "monthlyLimit", e.target.value)}
                    />
                  </div>
                  <span style={styles.limitHint}>Max per month</span>
                </div>
                <div style={styles.limitField}>
                  <label style={styles.limitLabel}>Quarterly Limit</label>
                  <div style={styles.inputWrapper}>
                    <span style={styles.inputPrefix}>₦</span>
                    <input
                      type="text"
                      className="limit-input"
                      style={styles.limitInput}
                      value={currentLimits.quarterlyLimit.toLocaleString()}
                      onChange={(e) => handleLimitChange(activeTab, "quarterlyLimit", e.target.value)}
                    />
                  </div>
                  <span style={styles.limitHint}>Max per quarter</span>
                </div>
              </div>
            </div>

            {/* Per Transaction Limits */}
            <div style={styles.limitSection}>
              <h4 style={styles.sectionTitle}>💳 Per Transaction Limits</h4>
              <div style={styles.limitRow}>
                <div style={styles.limitField}>
                  <label style={styles.limitLabel}>Minimum Amount</label>
                  <div style={styles.inputWrapper}>
                    <span style={styles.inputPrefix}>₦</span>
                    <input
                      type="text"
                      className="limit-input"
                      style={styles.limitInput}
                      value={currentLimits.perTransactionMin.toLocaleString()}
                      onChange={(e) => handleLimitChange(activeTab, "perTransactionMin", e.target.value)}
                    />
                  </div>
                  <span style={styles.limitHint}>Min per transaction</span>
                </div>
                <div style={styles.limitField}>
                  <label style={styles.limitLabel}>Maximum Amount</label>
                  <div style={styles.inputWrapper}>
                    <span style={styles.inputPrefix}>₦</span>
                    <input
                      type="text"
                      className="limit-input"
                      style={styles.limitInput}
                      value={currentLimits.perTransactionMax.toLocaleString()}
                      onChange={(e) => handleLimitChange(activeTab, "perTransactionMax", e.target.value)}
                    />
                  </div>
                  <span style={styles.limitHint}>Max per transaction</span>
                </div>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div style={styles.summarySection}>
            <h4 style={styles.sectionTitle}>📊 Limits Summary</h4>
            <div style={styles.summaryGrid}>
              <div style={styles.summaryCard}>
                <span style={styles.summaryLabel}>Daily</span>
                <span style={styles.summaryValue}>{formatCurrency(currentLimits.dailyLimit)}</span>
              </div>
              <div style={styles.summaryCard}>
                <span style={styles.summaryLabel}>Weekly</span>
                <span style={styles.summaryValue}>{formatCurrency(currentLimits.weeklyLimit)}</span>
              </div>
              <div style={styles.summaryCard}>
                <span style={styles.summaryLabel}>Monthly</span>
                <span style={styles.summaryValue}>{formatCurrency(currentLimits.monthlyLimit)}</span>
              </div>
              <div style={styles.summaryCard}>
                <span style={styles.summaryLabel}>Quarterly</span>
                <span style={styles.summaryValue}>{formatCurrency(currentLimits.quarterlyLimit)}</span>
              </div>
              <div style={styles.summaryCard}>
                <span style={styles.summaryLabel}>Min/Txn</span>
                <span style={styles.summaryValue}>{formatCurrency(currentLimits.perTransactionMin)}</span>
              </div>
              <div style={styles.summaryCard}>
                <span style={styles.summaryLabel}>Max/Txn</span>
                <span style={styles.summaryValue}>{formatCurrency(currentLimits.perTransactionMax)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div style={styles.quickActions}>
          <h4 style={styles.sectionTitle}>⚡ Quick Actions</h4>
          <div style={styles.actionButtons}>
            <button
              onClick={() => {
                const defaultLimits = {
                  dailyLimit: 100000,
                  weeklyLimit: 500000,
                  monthlyLimit: 2000000,
                  quarterlyLimit: 5000000,
                  perTransactionMin: 50,
                  perTransactionMax: 50000,
                  isEnabled: true
                };
                setLimits(prev => ({ ...prev, [activeTab]: defaultLimits }));
              }}
              style={styles.actionBtn}
            >
              🔄 Reset to Default
            </button>
            <button
              onClick={() => {
                const doubled = {
                  ...currentLimits,
                  dailyLimit: currentLimits.dailyLimit * 2,
                  weeklyLimit: currentLimits.weeklyLimit * 2,
                  monthlyLimit: currentLimits.monthlyLimit * 2,
                  quarterlyLimit: currentLimits.quarterlyLimit * 2,
                  perTransactionMax: currentLimits.perTransactionMax * 2
                };
                setLimits(prev => ({ ...prev, [activeTab]: doubled }));
              }}
              style={styles.actionBtn}
            >
              📈 Double Limits
            </button>
            <button
              onClick={() => {
                const halved = {
                  ...currentLimits,
                  dailyLimit: Math.floor(currentLimits.dailyLimit / 2),
                  weeklyLimit: Math.floor(currentLimits.weeklyLimit / 2),
                  monthlyLimit: Math.floor(currentLimits.monthlyLimit / 2),
                  quarterlyLimit: Math.floor(currentLimits.quarterlyLimit / 2),
                  perTransactionMax: Math.floor(currentLimits.perTransactionMax / 2)
                };
                setLimits(prev => ({ ...prev, [activeTab]: halved }));
              }}
              style={styles.actionBtn}
            >
              📉 Halve Limits
            </button>
            <button
              onClick={() => {
                const globalLimits = limits.global;
                setLimits(prev => ({
                  ...prev,
                  airtime: { ...globalLimits },
                  data: { ...globalLimits },
                  electricity: { ...globalLimits },
                  cableTv: { ...globalLimits }
                }));
              }}
              style={{...styles.actionBtn, background: "#1183FB", color: "white"}}
            >
              🌐 Apply Global to All
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}

const styles = {
  container: { minHeight: "100vh", background: "#f1f5f9", fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif" },
  loadingContainer: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", gap: "16px" },
  spinner: { width: "48px", height: "48px", border: "4px solid #e2e8f0", borderTopColor: "#1183FB", borderRadius: "50%", animation: "spin 1s linear infinite" },

  header: { background: "linear-gradient(135deg, #011745, #1183FB)", padding: "20px 32px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "16px" },
  headerLeft: { display: "flex", alignItems: "center", gap: "16px" },
  backBtn: { background: "rgba(255,255,255,0.2)", border: "none", borderRadius: "12px", color: "white", width: "44px", height: "44px", fontSize: "20px", cursor: "pointer" },
  headerTitle: { color: "white", fontSize: "24px", fontWeight: "700", margin: 0 },
  headerSubtitle: { color: "rgba(255,255,255,0.8)", fontSize: "14px", margin: "4px 0 0 0" },
  saveBtn: { background: "#10b981", border: "none", borderRadius: "12px", color: "white", padding: "14px 28px", fontSize: "15px", fontWeight: "600", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" },

  main: { padding: "24px 32px", maxWidth: "1200px", margin: "0 auto" },

  successAlert: { background: "#d1fae5", color: "#065f46", padding: "16px 20px", borderRadius: "12px", marginBottom: "20px", fontWeight: "500" },
  errorAlert: { background: "#fee2e2", color: "#991b1b", padding: "16px 20px", borderRadius: "12px", marginBottom: "20px", fontWeight: "500" },

  infoCard: { background: "#e0f2fe", borderRadius: "16px", padding: "20px", marginBottom: "24px", display: "flex", gap: "16px", alignItems: "flex-start" },
  infoIcon: { fontSize: "24px" },
  infoTitle: { margin: "0 0 8px 0", fontSize: "16px", fontWeight: "600", color: "#0369a1" },
  infoText: { margin: 0, fontSize: "14px", color: "#0369a1", lineHeight: "1.5" },

  tabsContainer: { display: "flex", gap: "12px", marginBottom: "24px", flexWrap: "wrap" },
  tabBtn: { display: "flex", alignItems: "center", gap: "8px", padding: "12px 20px", border: "2px solid #e2e8f0", borderRadius: "12px", fontSize: "14px", fontWeight: "600", cursor: "pointer", transition: "all 0.2s" },
  tabIcon: { fontSize: "18px" },

  configCard: { background: "white", borderRadius: "20px", padding: "28px", boxShadow: "0 4px 20px rgba(0,0,0,0.08)", marginBottom: "24px" },
  configHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px", paddingBottom: "20px", borderBottom: "2px solid #f1f5f9" },
  configTitleRow: { display: "flex", alignItems: "center", gap: "16px" },
  configIcon: { width: "50px", height: "50px", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "24px" },
  configTitle: { margin: 0, fontSize: "20px", fontWeight: "700", color: "#011745" },
  configSubtitle: { margin: "4px 0 0 0", fontSize: "14px", color: "#64748b" },

  disabledOverlay: { background: "#fef3c7", borderRadius: "12px", padding: "16px", marginBottom: "20px", textAlign: "center", color: "#92400e" },

  limitsGrid: { display: "flex", flexDirection: "column", gap: "24px" },
  limitSection: { background: "#f8fafc", borderRadius: "16px", padding: "20px" },
  sectionTitle: { margin: "0 0 16px 0", fontSize: "16px", fontWeight: "600", color: "#011745" },
  limitRow: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "20px" },
  limitField: { display: "flex", flexDirection: "column", gap: "8px" },
  limitLabel: { fontSize: "14px", fontWeight: "600", color: "#374151" },
  inputWrapper: { position: "relative", display: "flex", alignItems: "center" },
  inputPrefix: { position: "absolute", left: "16px", color: "#64748b", fontWeight: "600", fontSize: "16px" },
  limitInput: { width: "100%", padding: "14px 16px 14px 36px", border: "2px solid #e2e8f0", borderRadius: "10px", fontSize: "16px", fontWeight: "600", color: "#011745", background: "white", transition: "all 0.2s" },
  limitHint: { fontSize: "12px", color: "#94a3b8" },

  summarySection: { marginTop: "24px", paddingTop: "24px", borderTop: "2px solid #f1f5f9" },
  summaryGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "16px" },
  summaryCard: { background: "linear-gradient(135deg, #f8fafc, #f1f5f9)", borderRadius: "12px", padding: "16px", textAlign: "center" },
  summaryLabel: { display: "block", fontSize: "12px", color: "#64748b", fontWeight: "600", marginBottom: "8px" },
  summaryValue: { display: "block", fontSize: "18px", fontWeight: "700", color: "#011745" },

  quickActions: { background: "white", borderRadius: "20px", padding: "24px", boxShadow: "0 4px 20px rgba(0,0,0,0.08)" },
  actionButtons: { display: "flex", gap: "12px", flexWrap: "wrap" },
  actionBtn: { padding: "12px 20px", border: "2px solid #e2e8f0", borderRadius: "10px", fontSize: "14px", fontWeight: "600", cursor: "pointer", background: "white", color: "#374151", transition: "all 0.2s" }
};
